/*  ENTRANDO IN UNA CASELLA SE NE SELEZIONA IL CONTENUTO.

    «Così se scrivi te lo cancella già in automatico» (Andrea, 01/10/2026, in riunione dal
    cliente). È il gesto più ripetuto di tutta la compilazione di un preventivo: si entra
    in una casella che ha già un numero dentro, e quel numero va sostituito, non corretto.
    Senza selezione ci vogliono tre gesti — clic, seleziona tutto, scrivi — moltiplicati
    per ogni riga di ogni offerta.

    IN UN POSTO SOLO E NON CAMPO PER CAMPO. Le caselle sono qualche decina solo nella
    scheda del preventivo, e una `onFocus` ripetuta ovunque significa che la prossima
    casella scritta da qualcuno si comporterà diversamente dalle altre. Qui si ascolta
    alla radice e si decide da una regola: una casella si comporta come le sue sorelle
    perché è la stessa riga di codice a deciderlo.

    E VALE OVUNQUE, non solo sui preventivi. Una casella che seleziona nei preventivi e
    non nelle commesse non è una funzione in più: è una schermata che si comporta in modo
    imprevedibile, e chi la usa smette di fidarsi del gesto in tutte e due.

    Le due DECISIONI stanno sopra, in funzioni pure, e il collegamento agli eventi sotto:
    qui non c'è un ambiente con un DOM nelle prove — si rende il markup e si guarda — e
    senza questa divisione le due scelte che possono essere sbagliate (quali caselle, e
    clic contro trascinamento) resterebbero senza prova.
*/

/*  Le caselle che si comportano così: quelle dove si scrive un dato breve, e dove
    sostituire è più frequente che correggere.

    Fuori le aree di testo — una nota d'offerta è lunga, e un tasto premuto per sbaglio
    su tutto selezionato cancella un paragrafo. Fuori le spunte e le tendine, che non
    hanno un contenuto da selezionare. Fuori le password, che qui non ci sono ma un
    giorno ci saranno.  */
const TIPI_CON_SELEZIONE = new Set([
  'text', 'number', 'search', 'tel', 'url', 'email',
]);

export interface CasellaDescritta {
  tipo: string;
  soloLettura: boolean;
  spenta: boolean;
}

export function casellaDaSelezionare(casella: CasellaDescritta): boolean {
  if (casella.soloLettura || casella.spenta) return false;

  return TIPI_CON_SELEZIONE.has(casella.tipo);
}

export interface Punto {
  x: number;
  y: number;
}

/*  Quanto si può muovere il dito fra la pressione e il rilascio perché resti un clic.

    Non è pignoleria: trascinare dentro una casella vuol dire «voglio selezionare questo
    pezzo qui», ed è l'unico modo di correggere una cifra in mezzo a un numero lungo.
    Selezionare tutto al rilascio butterebbe via proprio quella selezione.

    Qualche pixel di tolleranza perché un clic fermo non esiste: il mouse si sposta di
    uno o due pixel mentre si preme, e un trascinamento vero ne fa decine.  */
const SCARTO_DEL_CLIC = 4;

export function eUnClic(pressione: Punto, rilascio: Punto): boolean {
  return Math.abs(rilascio.x - pressione.x) <= SCARTO_DEL_CLIC
    && Math.abs(rilascio.y - pressione.y) <= SCARTO_DEL_CLIC;
}

function vaSelezionato(elemento: EventTarget | null): elemento is HTMLInputElement {
  return elemento instanceof HTMLInputElement
    && casellaDaSelezionare({
      tipo: elemento.type,
      soloLettura: elemento.readOnly,
      spenta: elemento.disabled,
    });
}

/*  `select()` su una casella numerica può protestare in qualche browser, e una casella
    che non si lascia selezionare non è un motivo per rompere il resto della schermata.  */
function seleziona(campo: HTMLInputElement): void {
  try {
    campo.select();
  } catch {
    /* la casella resta com'era: il peggio che succede è un gesto in più */
  }
}

/*  Installa l'ascolto e torna la funzione per toglierlo.

    Tre eventi e non uno, perché arrivare con il tasto TAB e arrivare con il mouse sono
    due storie diverse:

    - col TAB il fuoco arriva e basta, e lì si seleziona subito;
    - col mouse il fuoco arriva A METÀ del gesto, fra la pressione e il rilascio.
      Selezionare lì verrebbe disfatto dal rilascio, che posa il cursore dove si è
      cliccato. Quindi si aspetta il rilascio, e si seleziona solo se il dito non si è
      mosso.

    Un secondo clic su una casella già attiva NON riseleziona: a quel punto si è dentro, e
    si sta posando il cursore per correggere. È lo stesso comportamento della barra degli
    indirizzi del browser, che è dove tutti l'hanno imparato.  */
export function installaSelezioneEntrando(radice: Document | HTMLElement = document): () => void {
  let premuto: { campo: HTMLInputElement; da: Punto } | null = null;

  const allaPressione = (evento: Event) => {
    const e = evento as MouseEvent;
    premuto =
      vaSelezionato(e.target) && e.target !== document.activeElement
        ? { campo: e.target, da: { x: e.clientX, y: e.clientY } }
        : null;
  };

  const alRilascio = (evento: Event) => {
    const e = evento as MouseEvent;
    const atteso = premuto;
    premuto = null;

    if (atteso === null || e.target !== atteso.campo) return;
    if (!eUnClic(atteso.da, { x: e.clientX, y: e.clientY })) return;

    seleziona(atteso.campo);
  };

  const alFuoco = (evento: Event) => {
    //  Fuoco arrivato mentre il mouse è premuto: ci pensa il rilascio.
    if (premuto !== null) return;
    if (vaSelezionato(evento.target)) seleziona(evento.target);
  };

  radice.addEventListener('mousedown', allaPressione, true);
  radice.addEventListener('mouseup', alRilascio, true);
  radice.addEventListener('focusin', alFuoco);

  return () => {
    radice.removeEventListener('mousedown', allaPressione, true);
    radice.removeEventListener('mouseup', alRilascio, true);
    radice.removeEventListener('focusin', alFuoco);
  };
}
